import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CanvasAggregationCacheEntryModel,
  CanvasAggregationValidatorStatus,
  MappingResponse,
} from '../../services/api';
import type { CanvasGraph } from '../types';
import type { AggregationOverlayLegendStep } from '../AggregationOverlayLegend';

const AGGREGATION_OVERLAY_COLORS = [
  '#0ea5e9',
  '#3b82f6',
  '#f97316',
  '#22c55e',
  '#ef4444',
  '#06b6d4',
  '#f59e0b',
  '#14b8a6',
];

export type AggregationOverlayNodeConfig = {
  aggregationId: string;
  color: string;
  displayOrder: number;
  badgeLabel: string;
  validatorStatus: CanvasAggregationValidatorStatus;
  cacheKey?: string;
  sheetId?: string;
  lastComputedAt?: string | null;
  lastValidatedAt?: string | null;
  validatorMessage?: string | null;
  fingerprint?: string | null;
};

export type AggregationTelemetrySummary = {
  total: number;
  validated: number;
  failed: number;
  pending: number;
  missingCache: number;
  lastComputedAt?: string | null;
  lastValidatedAt?: string | null;
};

const expandAggregationNodeIds = (aggregationId: string): string[] => {
  if (!aggregationId) return [];
  const bare = aggregationId.replace(/^aggregation:/, '');
  return Array.from(new Set([aggregationId, bare, `aggregation:${bare}`]));
};

const normaliseAggregationNodeId = (nodeId: string): string =>
  (nodeId || '').replace(/^aggregation:/, '');

export interface UseAggregationOverlayOptions {
  canvasGraph: CanvasGraph;
  mappingResponse?: MappingResponse;
  activeWorkbookId?: string;
  recordCanvasEvent: (kind: string, payload: Record<string, string>) => void;
}

export function useAggregationOverlay({
  canvasGraph,
  mappingResponse,
  activeWorkbookId,
  recordCanvasEvent,
}: UseAggregationOverlayOptions) {
  const [aggregationOverlayEnabled, setAggregationOverlayEnabled] = useState(false);
  const [focusedAggregationId, setFocusedAggregationId] = useState<string | null>(null);

  const aggregationPlan = mappingResponse?.metadata?.aggregation_plan ?? [];
  const aggregationCache = (mappingResponse?.metadata?.aggregation_cache ?? {}) as Record<
    string,
    CanvasAggregationCacheEntryModel
  >;

  const aggregationOverlayAvailable = useMemo(() => {
    if (!canvasGraph || aggregationPlan.length === 0) return false;
    return canvasGraph.nodes.some((node) => node.data?.kind === 'aggregation');
  }, [canvasGraph, aggregationPlan]);

  const aggregationOverlayArtifacts = useMemo(() => {
    if (!aggregationOverlayEnabled || !canvasGraph || aggregationPlan.length === 0) {
      return {
        configMap: new Map<string, AggregationOverlayNodeConfig>(),
        legendSteps: [] as AggregationOverlayLegendStep[],
      };
    }

    const configMap = new Map<string, AggregationOverlayNodeConfig>();
    const legendSteps: AggregationOverlayLegendStep[] = [];

    const sortedPlan = [...aggregationPlan].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    sortedPlan.forEach((planStep, index) => {
      if (!planStep?.aggregation_id) return;
      const normalized = normaliseAggregationNodeId(planStep.aggregation_id);
      const color = AGGREGATION_OVERLAY_COLORS[index % AGGREGATION_OVERLAY_COLORS.length];
      const badgeLabel = `S${index + 1}`;

      let cacheEntry: CanvasAggregationCacheEntryModel | undefined =
        aggregationCache[planStep.aggregation_id];
      if (!cacheEntry) {
        const candidates = expandAggregationNodeIds(planStep.aggregation_id);
        cacheEntry = candidates
          .map((candidate) => aggregationCache[candidate])
          .find(Boolean);
      }

      const validatorStatus: CanvasAggregationValidatorStatus =
        cacheEntry?.validator_status ?? 'pending';
      const nodeMatch = canvasGraph.nodes.find(
        (node) =>
          node.data?.kind === 'aggregation' &&
          normaliseAggregationNodeId(node.id) === normalized,
      );
      const nodeLabel = nodeMatch?.data?.label?.trim();
      const operation = nodeMatch?.data?.aggregation?.operation?.trim();
      const labelParts = [nodeLabel, operation].filter(
        (value, idx, array) =>
          typeof value === 'string' &&
          value.length > 0 &&
          array.indexOf(value) === idx,
      ) as string[];
      const legendLabel =
        labelParts.length > 0
          ? labelParts.join(' • ')
          : `Aggregation ${index + 1}`;

      const config: AggregationOverlayNodeConfig = {
        aggregationId: normalized,
        color,
        displayOrder: index,
        badgeLabel,
        validatorStatus,
        cacheKey: cacheEntry?.cache_key ?? planStep.cache_key,
        sheetId: planStep.sheet_id,
        lastComputedAt: cacheEntry?.last_computed_at ?? null,
        lastValidatedAt: cacheEntry?.last_validated_at ?? null,
        validatorMessage: cacheEntry?.validator_message ?? null,
        fingerprint: cacheEntry?.fingerprint ?? null,
      };
      configMap.set(normalized, config);

      legendSteps.push({
        aggregationId: normalized,
        order: planStep.order,
        label: legendLabel,
        color,
        sheetId: planStep.sheet_id,
        validatorStatus,
        inputs: planStep.inputs ?? [],
        outputs: planStep.outputs ?? [],
        cacheKey: cacheEntry?.cache_key ?? planStep.cache_key,
        lastComputedAt: cacheEntry?.last_computed_at ?? null,
        lastValidatedAt: cacheEntry?.last_validated_at ?? null,
        validatorMessage: cacheEntry?.validator_message ?? null,
        fingerprint: cacheEntry?.fingerprint ?? null,
      });
    });

    legendSteps.sort((a, b) => a.order - b.order);
    return { configMap, legendSteps };
  }, [aggregationOverlayEnabled, aggregationPlan, aggregationCache, canvasGraph]);

  const aggregationOverlayConfigMap = aggregationOverlayArtifacts.configMap;
  const aggregationOverlayLegendSteps = aggregationOverlayArtifacts.legendSteps;
  const aggregationOverlayActive =
    aggregationOverlayEnabled &&
    aggregationOverlayAvailable &&
    aggregationOverlayConfigMap.size > 0;

  const aggregationTelemetrySummary = useMemo<AggregationTelemetrySummary | null>(() => {
    if (!aggregationPlan.length) return null;

    const summary: AggregationTelemetrySummary = {
      total: 0,
      validated: 0,
      failed: 0,
      pending: 0,
      missingCache: 0,
      lastComputedAt: null,
      lastValidatedAt: null,
    };

    const selectLatest = (
      current: string | null | undefined,
      candidate?: string | null,
    ) => {
      if (!candidate) return current ?? null;
      const candidateTime = Date.parse(candidate);
      if (Number.isNaN(candidateTime)) return current ?? null;
      if (!current) return new Date(candidateTime).toISOString();
      const currentTime = Date.parse(current);
      if (Number.isNaN(currentTime) || candidateTime > currentTime)
        return new Date(candidateTime).toISOString();
      return current;
    };

    aggregationPlan.forEach((planStep) => {
      if (!planStep?.aggregation_id) return;
      summary.total += 1;
      let cacheEntry: CanvasAggregationCacheEntryModel | undefined =
        aggregationCache[planStep.aggregation_id];
      if (!cacheEntry) {
        const candidates = expandAggregationNodeIds(planStep.aggregation_id);
        cacheEntry = candidates
          .map((candidate) => aggregationCache[candidate])
          .find(Boolean);
      }
      if (!cacheEntry) {
        summary.pending += 1;
        summary.missingCache += 1;
        return;
      }
      const status = cacheEntry.validator_status ?? 'pending';
      if (status === 'validated') summary.validated += 1;
      else if (status === 'failed') summary.failed += 1;
      else summary.pending += 1;
      summary.lastComputedAt = selectLatest(
        summary.lastComputedAt,
        cacheEntry.last_computed_at ?? null,
      );
      summary.lastValidatedAt = selectLatest(
        summary.lastValidatedAt,
        cacheEntry.last_validated_at ?? null,
      );
    });

    return summary;
  }, [aggregationPlan, aggregationCache]);

  // Clear focused aggregation when overlay disabled
  useEffect(() => {
    if (!aggregationOverlayEnabled && focusedAggregationId) {
      setFocusedAggregationId(null);
    }
  }, [aggregationOverlayEnabled, focusedAggregationId]);

  useEffect(() => {
    if (!aggregationOverlayActive && focusedAggregationId) {
      setFocusedAggregationId(null);
    }
  }, [aggregationOverlayActive, focusedAggregationId]);

  useEffect(() => {
    if (!focusedAggregationId) return;
    if (!aggregationOverlayConfigMap.has(focusedAggregationId)) {
      setFocusedAggregationId(null);
    }
  }, [focusedAggregationId, aggregationOverlayConfigMap]);

  const handleFocusAggregation = useCallback(
    (aggregationId: string | null) => {
      setFocusedAggregationId(aggregationId);
      recordCanvasEvent('canvas.overlay.aggregation.focus', {
        workbook_id: activeWorkbookId ?? '',
        aggregation_id: aggregationId ?? 'all',
      });
    },
    [recordCanvasEvent, activeWorkbookId],
  );

  const handleToggleAggregationOverlay = useCallback(() => {
    if (!aggregationOverlayAvailable) return;
    const nextState = !aggregationOverlayEnabled;
    setAggregationOverlayEnabled(nextState);
    recordCanvasEvent('canvas.overlay.aggregation', {
      workbook_id: activeWorkbookId ?? '',
      state: nextState ? 'enabled' : 'disabled',
    });
  }, [
    aggregationOverlayAvailable,
    aggregationOverlayEnabled,
    recordCanvasEvent,
    activeWorkbookId,
  ]);

  return {
    aggregationOverlayEnabled,
    setAggregationOverlayEnabled,
    focusedAggregationId,
    aggregationOverlayAvailable,
    aggregationOverlayActive,
    aggregationOverlayConfigMap,
    aggregationOverlayLegendSteps,
    aggregationTelemetrySummary,
    handleFocusAggregation,
    handleToggleAggregationOverlay,
    normaliseAggregationNodeId,
  };
}

export { normaliseAggregationNodeId, expandAggregationNodeIds };
